import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useQuery } from '@tanstack/react-query';

import { getRequestProjectUrl, getSpotlightUrl } from '@/api/v4/account.api';
import { QueryKey } from '@/config/query-client';
import { useSpotlight } from '@/hooks/use-spotlight';
import { mediaQuery } from '@/theme/setupTheme';
import { notify } from '@/ui/snackbar/notify';

import { RequestSolutionIcon, ShareSpotlightIcon } from './share-link-icons';
import { ShareLinkCard } from './share-link-card';

export const General = () => {
  const { t } = useTranslation('settings');
  const { hasSpotlight } = useSpotlight();

  const { data: requestProjectUrlData, isLoading: isRequestProjectUrlLoading } =
    useQuery({
      queryFn: getRequestProjectUrl,
      queryKey: [QueryKey.RequestProjectUrl],
    });

  const { data: spotlightUrlData, isLoading: isSpotlightUrlLoading } = useQuery(
    {
      enabled: hasSpotlight,
      queryFn: getSpotlightUrl,
      queryKey: [QueryKey.SpotlightUrl],
    },
  );

  const copyToClipboard = async (url: string | undefined) => {
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      notify({ message: t`generalTab.copiedToClipboard` });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <GeneralContainer>
      <SectionContainer>
        <SectionTitle>{t`generalTab.shareAndInvite`}</SectionTitle>

        <CardsGrid>
          <ShareLinkCard
            description={t`generalTab.requestASolutionDescription`}
            icon={<RequestSolutionIcon />}
            isCopyDisabled={
              isRequestProjectUrlLoading ||
              !requestProjectUrlData?.requestProjectUrl
            }
            onCopy={() =>
              copyToClipboard(requestProjectUrlData?.requestProjectUrl)
            }
            title={t`generalTab.requestASolution`}
          />

          {hasSpotlight ? (
            <ShareLinkCard
              description={t`generalTab.shareSpotlightPageDescription`}
              icon={<ShareSpotlightIcon />}
              isCopyDisabled={
                isSpotlightUrlLoading || !spotlightUrlData?.spotlightUrl
              }
              onCopy={() => copyToClipboard(spotlightUrlData?.spotlightUrl)}
              title={t`generalTab.shareSpotlightPage`}
            />
          ) : null}
        </CardsGrid>
      </SectionContainer>
    </GeneralContainer>
  );
};

const GeneralContainer = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 8px;
`;

const SectionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SectionTitle = styled('h2')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  margin: 0;
`;

const CardsGrid = styled('div')`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;

  ${mediaQuery('md', 'min')} {
    grid-template-columns: repeat(2, minmax(0, 401px));
    justify-content: start;
  }
`;
